import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { MapPin, Users, Award, Heart, ZoomIn, X } from "lucide-react";
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";
import resortInfra from "@/assets/resort-infrastructure.jpg";
import portoTown from "@/assets/porto-galinhas-town.jpg";
import { useAuthState } from "@/hooks/useAuthState";

const Sobre = () => {
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={userType === "owner"} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroMuroAlto1})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40" />
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg">
            Sobre a Live Stay
          </h1>
          <p className="text-xl text-white/95 drop-shadow-md max-w-2xl mx-auto">
            Conectando você ao paraíso de Muro Alto
          </p>
        </div>
      </section>

      {/* Nossa História */}
      <section className="py-16 container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Nossa História</h2>
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p className="text-lg leading-relaxed mb-6">
              A <strong className="text-foreground">Live Stay</strong> nasceu da paixão por proporcionar experiências 
              únicas em um dos destinos mais paradisíacos do Brasil: Porto de Galinhas. Nossa missão é conectar 
              viajantes que buscam conforto, exclusividade e contato com a natureza ao empreendimento 
              Porto 2 Life Resort, localizado na prestigiada Praia de Muro Alto.
            </p>
            <p className="text-lg leading-relaxed mb-6">
              Com anos de experiência no mercado de hospitalidade, entendemos que cada viagem é especial e merece 
              ser inesquecível. Por isso, selecionamos cuidadosamente o melhor que Muro Alto tem a oferecer: 
              infraestrutura de primeira linha, acesso privilegiado às famosas piscinas naturais e o conforto 
              de um resort moderno e completo.
            </p>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Nossos Valores</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center border shadow-soft hover:shadow-medium transition-all">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Excelência</h3>
                <p className="text-muted-foreground">
                  Comprometidos em oferecer apenas o melhor em hospedagem e atendimento
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border shadow-soft hover:shadow-medium transition-all">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/10 flex items-center justify-center">
                  <Heart className="h-8 w-8 text-secondary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Hospitalidade</h3>
                <p className="text-muted-foreground">
                  Tratamos cada hóspede com o carinho e atenção que merece
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border shadow-soft hover:shadow-medium transition-all">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                  <MapPin className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Localização</h3>
                <p className="text-muted-foreground">
                  O melhor de Muro Alto, com acesso direto às piscinas naturais
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border shadow-soft hover:shadow-medium transition-all">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Experiência</h3>
                <p className="text-muted-foreground">
                  Momentos inesquecíveis em família ou com amigos
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Porto 2 Life Resort */}
      <section className="py-16 container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
          Porto 2 Life Resort - Nosso Empreendimento
        </h2>
        
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src={resortInfra} 
              alt="Porto 2 Life Resort" 
              className="rounded-lg shadow-large w-full h-[400px] object-cover"
            />
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-4">O Maior da GAV Resorts</h3>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              O <strong className="text-foreground">Porto 2 Life Resort</strong> é o maior empreendimento 
              da GAV Resorts, previsto para 2025. Com arquitetura moderna e inovadora, o resort enaltece 
              a natureza exuberante de Muro Alto enquanto oferece toda a infraestrutura que você merece.
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Localizado na Praia de Muro Alto, em Porto de Galinhas - PE, o empreendimento oferece 
              uma experiência completa de resort, com apartamentos totalmente mobiliados e equipados 
              com todos os utensílios e acessórios necessários para sua estadia.
            </p>
          </div>
        </div>

        {/* Infraestrutura */}
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 md:p-12">
          <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            Infraestrutura Completa
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2">Área de Lazer Gigante</h4>
                <p className="text-sm text-muted-foreground">
                  Piscinas de diversos tamanhos, piscinas de borda infinita e toboágua
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2">Para Toda Família</h4>
                <p className="text-sm text-muted-foreground">
                  Prainha infantil, campo de minigolfe e salão de jogos
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2">Gastronomia</h4>
                <p className="text-sm text-muted-foreground">
                  Restaurante Japonês inédito e Pâtisserie exclusiva
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2">Wellness</h4>
                <p className="text-sm text-muted-foreground">
                  Saunas com vista panorâmica para relaxamento total
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2">Esportes</h4>
                <p className="text-sm text-muted-foreground">
                  Quadras poliesportiva, de areia, tênis e muito mais
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h4 className="font-semibold text-lg mb-2">Entretenimento</h4>
                <p className="text-sm text-muted-foreground">
                  Karaokê e Palco Aquático para momentos especiais
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Porto de Galinhas */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            Porto de Galinhas - Destino Paradisíaco
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1">
              <h3 className="text-2xl font-bold mb-4">História e Belezas Naturais</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Porto de Galinhas, localizada no município de Ipojuca em Pernambuco, é uma das praias 
                mais famosas e bonitas do Brasil. Por 10 anos consecutivos foi eleita a melhor praia 
                do Brasil pela Revista Viagem e Turismo.
              </p>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                O destino é conhecido internacionalmente por suas piscinas naturais de águas mornas e 
                cristalinas, repletas de peixes coloridos entre recifes de coral. A região oferece 
                infraestrutura completa com os melhores hotéis e resorts do Brasil, gastronomia de 
                alta qualidade e um povo extremamente acolhedor.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Situada a apenas 50 km do Aeroporto Internacional do Recife, Porto de Galinhas reúne 
                praias paradisíacas, passeios inesquecíveis e toda comodidade que você precisa para 
                suas férias dos sonhos.
              </p>
            </div>
            <div className="order-1 md:order-2">
              <img 
                src={portoTown} 
                alt="Porto de Galinhas" 
                className="rounded-lg shadow-large w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Mapa do Empreendimento</h2>
          <div className="max-w-5xl mx-auto">
            <Card className="border-0 shadow-large overflow-hidden">
              <div className="w-full relative group cursor-pointer" onClick={() => setIsMapModalOpen(true)}>
                <img
                  src="/mapa.jpg"
                  alt="Localização do Porto 2 Life Resort - Praia de Muro Alto, Porto de Galinhas - PE"
                  className="w-full h-auto object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 bg-white/90 px-4 py-2 rounded-lg">
                    <ZoomIn className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium text-foreground">Clique para ampliar</span>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Modal de Zoom do Mapa */}
            <Dialog open={isMapModalOpen} onOpenChange={setIsMapModalOpen}>
              <DialogContent className="max-w-7xl w-full p-0 bg-black/95">
                <div className="relative w-full h-[90vh] flex items-center justify-center overflow-auto">
                  <button
                    onClick={() => setIsMapModalOpen(false)}
                    className="absolute top-4 right-4 z-20 bg-white/90 hover:bg-white text-foreground rounded-full p-2 transition-colors shadow-lg"
                    aria-label="Fechar"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <div className="relative inline-block max-w-full max-h-full">
                    <img
                      src="/mapa.jpg"
                      alt="Mapa do Empreendimento Porto 2 Life Resort - Ampliado"
                      className="max-w-full max-h-[90vh] object-contain"
                    />
                    {/* Overlay para demarcar os 8 blocos do empreendimento */}
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Bloco 1 - Superior Esquerdo */}
                      <div 
                        className="absolute border-3 border-primary bg-primary/20 rounded-lg shadow-lg"
                        style={{
                          top: '15%',
                          left: '8%',
                          width: '18%',
                          height: '30%',
                        }}
                      >
                        <div className="absolute -top-7 left-0 bg-primary text-white text-xs px-3 py-1.5 rounded-md font-bold shadow-md">
                          Bloco 1
                        </div>
                      </div>
                      {/* Bloco 2 - Superior Centro Esquerdo */}
                      <div 
                        className="absolute border-3 border-primary bg-primary/20 rounded-lg shadow-lg"
                        style={{
                          top: '15%',
                          left: '30%',
                          width: '18%',
                          height: '30%',
                        }}
                      >
                        <div className="absolute -top-7 left-0 bg-primary text-white text-xs px-3 py-1.5 rounded-md font-bold shadow-md">
                          Bloco 2
                        </div>
                      </div>
                      {/* Bloco 3 - Superior Centro Direito */}
                      <div 
                        className="absolute border-3 border-primary bg-primary/20 rounded-lg shadow-lg"
                        style={{
                          top: '15%',
                          left: '52%',
                          width: '18%',
                          height: '30%',
                        }}
                      >
                        <div className="absolute -top-7 left-0 bg-primary text-white text-xs px-3 py-1.5 rounded-md font-bold shadow-md">
                          Bloco 3
                        </div>
                      </div>
                      {/* Bloco 4 - Superior Direito */}
                      <div 
                        className="absolute border-3 border-primary bg-primary/20 rounded-lg shadow-lg"
                        style={{
                          top: '15%',
                          left: '74%',
                          width: '18%',
                          height: '30%',
                        }}
                      >
                        <div className="absolute -top-7 left-0 bg-primary text-white text-xs px-3 py-1.5 rounded-md font-bold shadow-md">
                          Bloco 4
                        </div>
                      </div>
                      {/* Bloco 5 - Inferior Esquerdo */}
                      <div 
                        className="absolute border-3 border-primary bg-primary/20 rounded-lg shadow-lg"
                        style={{
                          top: '50%',
                          left: '8%',
                          width: '18%',
                          height: '30%',
                        }}
                      >
                        <div className="absolute -top-7 left-0 bg-primary text-white text-xs px-3 py-1.5 rounded-md font-bold shadow-md">
                          Bloco 5
                        </div>
                      </div>
                      {/* Bloco 6 - Inferior Centro Esquerdo */}
                      <div 
                        className="absolute border-3 border-primary bg-primary/20 rounded-lg shadow-lg"
                        style={{
                          top: '50%',
                          left: '30%',
                          width: '18%',
                          height: '30%',
                        }}
                      >
                        <div className="absolute -top-7 left-0 bg-primary text-white text-xs px-3 py-1.5 rounded-md font-bold shadow-md">
                          Bloco 6
                        </div>
                      </div>
                      {/* Bloco 7 - Inferior Centro Direito */}
                      <div 
                        className="absolute border-3 border-primary bg-primary/20 rounded-lg shadow-lg"
                        style={{
                          top: '50%',
                          left: '52%',
                          width: '18%',
                          height: '30%',
                        }}
                      >
                        <div className="absolute -top-7 left-0 bg-primary text-white text-xs px-3 py-1.5 rounded-md font-bold shadow-md">
                          Bloco 7
                        </div>
                      </div>
                      {/* Bloco 8 - Inferior Direito */}
                      <div 
                        className="absolute border-3 border-primary bg-primary/20 rounded-lg shadow-lg"
                        style={{
                          top: '50%',
                          left: '74%',
                          width: '18%',
                          height: '30%',
                        }}
                      >
                        <div className="absolute -top-7 left-0 bg-primary text-white text-xs px-3 py-1.5 rounded-md font-bold shadow-md">
                          Bloco 8
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p className="flex items-center justify-center gap-2 mb-2">
                <MapPin className="h-4 w-4" />
                <strong>Porto 2 Life Resort</strong> - Praia de Muro Alto, Porto de Galinhas - PE
              </p>
              <p className="mb-2">
                <a
                  href="https://www.google.com/maps?q=-8.419796985795106,-34.97438954480893"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Ver no Google Maps
                </a>
                <span className="mx-2">•</span>
                Coordenadas: -8.419797, -34.974390
              </p>
              <p>A 50 km do Aeroporto Internacional do Recife</p>
              <p>A 5 km do centro de Porto de Galinhas</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para Viver Sua Melhor Experiência?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Entre em contato conosco e descubra como transformar suas férias em momentos inesquecíveis 
            no paraíso de Muro Alto.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/fale-conosco" 
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8"
            >
              Fale Conosco
            </a>
            <a 
              href="/busca?empreendimento=Porto 2 Life" 
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-11 px-8"
            >
              Ver Disponibilidade
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Sobre;